import { useCardPdpDataQuery } from './ProductDetailsPage.graphql.generated';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { findMatchingMarketingData, resetCardTemplateDefaults } from '../DesignsGallery/utils';
import type { ValidDigitalDesignApiData } from '../DesignsGallery/DesignsGallery.types';
import { SAVE_THE_DATE_PHOTO_THEMES } from '@apps/admin/saveTheDate/constants';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useStationeryProductDetailsPageHealth } from './ProductDetails.health';
import { useUpdatePdpUrlToIncludeTemplateId } from './useUpdatePdpUrlToIncludeTemplateId';
import { useAutofill } from '@apps/card/contexts/AutofillContext';
import { CARD_PREVIEW_IMAGE_KEY } from '@apps/card/hooks/useCardPreviewWithPhoto';
import { autofillPhotos as injectPreviewPhotos } from '@apps/card/utils/injectPhotosIntoTemplate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { getPreviewImageFromStorage } from '@apps/admin/components/PreviewWithPhoto/getPreviewImageFromStorage';
import { useEventInfo } from '@shared/utils/eventInfo';

export type DigitalDesignTemplate = ValidDigitalDesignApiData;

type UsePdpDataArgs = {
  themeId: string;
  category: StationeryTemplateCategoryEnum;
  enableRedirectIfInvalid?: boolean;
  templateId?: Maybe<string>;
  enableUpdateUrlToIncludeTemplateId?: boolean;
};

export const usePdpData = (args: UsePdpDataArgs) => {
  const { themeId, category, templateId, enableRedirectIfInvalid = false, enableUpdateUrlToIncludeTemplateId = false } = args;
  const { redirectToCategoryShopAs404 } = useCardTelemetry();
  const { goToShop } = useCardsRouterContext();
  const {
    data,
    loading: dataLoading,
    error,
  } = useCardPdpDataQuery({
    batchMode: 'fast',
    variables: {
      themeId,
      category,
      skipDigitalDesign: category === StationeryTemplateCategoryEnum.thankYou,
    },
    ssr: true,
  });

  const { onLoadComplete, onLoadFailure } = useStationeryProductDetailsPageHealth();
  const { autofill, autofillPhotos, loading: autofillLoading, photosLoading } = useAutofill();
  const loading = dataLoading || autofillLoading || photosLoading;

  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');
  const { eventInfo } = useEventInfo();

  const template = useMemo(() => {
    const templates = data?.stationeryTemplatesByThemeId ?? [];
    const template = templates.find(template => template.id === (templateId ?? '')) ?? templates[0];
    if (!template) return null;

    const clonedTemplate = cloneDeep(template);
    resetCardTemplateDefaults(clonedTemplate);
    autofill(clonedTemplate, 'trackEvent');

    // Read preview photos directly from localStorage — the full useCardPreviewWithPhoto hook is not
    // needed here since the PDP is a separate route (gallery already manages the hook + GQL query).
    const uploadedPreviewImages = previewWithPhotoEnabled && eventInfo?.eventId ? (getPreviewImageFromStorage(CARD_PREVIEW_IMAGE_KEY, eventInfo.eventId) ?? []) : [];

    if (uploadedPreviewImages.length > 0 && !clonedTemplate.themeId?.startsWith('upload_your_')) {
      // Apply the user's preview-with-photo selection to the template (same injection the gallery
      // thumbnails use) so the customized card / created draft keeps those photos.
      injectPreviewPhotos(
        clonedTemplate,
        uploadedPreviewImages.map(p => ({ url: p.url })),
      );
    } else if (!previewWithPhotoEnabled) {
      // Only auto-fill from album when preview mode is off — mirrors the gallery thumbnail
      // behavior so the PDP card matches what the user saw while browsing.
      autofillPhotos(clonedTemplate);
    }
    return clonedTemplate;
  }, [data?.stationeryTemplatesByThemeId, templateId, autofill, autofillPhotos, previewWithPhotoEnabled, eventInfo?.eventId]);

  useEffect(() => {
    if (error) {
      onLoadFailure(error);
    } else if (template?.id) {
      onLoadComplete();
    }
  }, [template, error, onLoadComplete, onLoadFailure]);

  useUpdatePdpUrlToIncludeTemplateId({
    themeId,
    templateCategory: category,
    templateMatches: data?.stationeryTemplatesByThemeId || [],
    enable: enableUpdateUrlToIncludeTemplateId,
  });

  const isPremiumDesign = !!template?.premium;

  const digitalTemplate: DigitalDesignTemplate | null = useMemo(() => {
    if (data?.ecardDesign) {
      const ecardDesign = data.ecardDesign;
      const maybeMatchingMarketingData = findMatchingMarketingData(ecardDesign, category);
      if (maybeMatchingMarketingData) {
        return {
          themeId: ecardDesign.themeId,
          name: ecardDesign.name,
          thumbnailUrl: maybeMatchingMarketingData.imageUrl,
          category: category as Extract<StationeryTemplateCategoryEnum, 'saveTheDate' | 'invitation'>,
          isPremiumDesign,
        };
      }
    }

    if (category === StationeryTemplateCategoryEnum.saveTheDate) {
      // TODO: photo ecards should be sent from card_service
      // https://withjoy.atlassian.net/browse/PRNT-1107
      const saveTheDateTheme = SAVE_THE_DATE_PHOTO_THEMES.find(theme => theme.themeId === themeId);
      if (saveTheDateTheme) {
        return {
          themeId: saveTheDateTheme.themeId,
          name: saveTheDateTheme.name,
          thumbnailUrl: saveTheDateTheme.thumbnailUrl,
          category,
          isPremiumDesign,
        };
      }
    }
    return null;
  }, [data, category, themeId, isPremiumDesign]);

  const hasTemplate = !!(template || digitalTemplate);
  const shouldRedirectBackToCategoryShop = enableRedirectIfInvalid && !hasTemplate && !loading;

  useEffect(() => {
    if (shouldRedirectBackToCategoryShop) {
      redirectToCategoryShopAs404({ page: 'pdp', themeId });
      goToShop('wedding', category);
    }
  }, [category, goToShop, redirectToCategoryShopAs404, themeId, shouldRedirectBackToCategoryShop]);

  return {
    loading,
    template,
    digitalTemplate,
  };
};
